import dayjs from 'dayjs';

/** Filter state with nothing active (mirrors the slice's bankFilters). */
export const EMPTY_BANK_FILTERS = {
  formatKeys: [],
  authorIds: [],
  dateRange: null,
  mediaTagIds: [],
};

/** Quick presets for the "Lisätty" (added) date facet. */
export const DATE_PRESETS = [
  { key: 'today', labelKey: 'bank.date_preset_today' },
  { key: '7d', labelKey: 'bank.date_preset_7d' },
  { key: '30d', labelKey: 'bank.date_preset_30d' },
  { key: 'year', labelKey: 'bank.date_preset_year' },
  { key: 'all', labelKey: 'bank.date_preset_all' },
];

/**
 * Resolves a date preset into a `{start, end}` millisecond range: start is
 * the start of the lower-bound day ('7d' / '30d' = that many days back,
 * 'year' = 1 January), end is the end of today. 'all' (and unknown keys)
 * return null, meaning "no range".
 * @param {string} presetKey one of DATE_PRESETS keys
 * @param {number} [nowMs=Date.now()] reference time in ms
 * @returns {{start: number, end: number}|null}
 */
export function getDatePresetRange(presetKey, nowMs = Date.now()) {
  const now = dayjs(nowMs);
  const end = now.endOf('day').valueOf();
  switch (presetKey) {
    case 'today':
      return { start: now.startOf('day').valueOf(), end };
    case '7d':
      return { start: now.subtract(7, 'day').startOf('day').valueOf(), end };
    case '30d':
      return { start: now.subtract(30, 'day').startOf('day').valueOf(), end };
    case 'year':
      return { start: now.startOf('year').valueOf(), end };
    default:
      return null;
  }
}

/**
 * The preset whose range equals `range` for the reference time: 'all' for no
 * range, one of the DATE_PRESETS keys when the range matches that preset
 * exactly (start and end), null for a hand-picked range.
 * @param {{start?: number, end?: number}|null} range
 * @param {number} [nowMs=Date.now()] reference time in ms
 * @returns {string|null}
 */
export function getActiveDatePreset(range, nowMs = Date.now()) {
  if (!range || typeof range !== 'object') return 'all';
  const match = DATE_PRESETS.find(({ key }) => {
    const preset = getDatePresetRange(key, nowMs);
    return preset && preset.start === range.start && preset.end === range.end;
  });
  return match ? match.key : null;
}

const toBoundary = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

/**
 * A date range only filters when it does not cover every item in the folder:
 * DateRangeFilter fires the full `[bounds.min, bounds.max]` range on mount.
 * @param {{start?: number, end?: number}|null} range
 * @param {{min: number, max: number}|null} bounds createdAtMs bounds of the folder
 * @returns {boolean}
 */
export function isDateRangeActive(range, bounds) {
  if (!range || typeof range !== 'object') return false;
  if (!bounds || typeof bounds !== 'object') return false;
  if (!Number.isFinite(bounds.min) || !Number.isFinite(bounds.max)) {
    return false;
  }
  const start = toBoundary(range.start, -Infinity);
  const end = toBoundary(range.end, Infinity);
  return !(start <= bounds.min && end >= bounds.max);
}

/**
 * Normalizes a search box value for matching against item.searchText.
 * @param {string} text
 * @returns {string} trimmed, lowercased
 */
export function normalizeSearchText(text) {
  return typeof text === 'string' ? text.trim().toLowerCase() : '';
}

const hasEntries = (list) => Array.isArray(list) && list.length > 0;

/**
 * Number of active facets (type, author, media tags, date, and the AI
 * conversation's photo filter), for the "Suodattimet (n)" badge. Search text
 * does not count.
 * @param {Object} filters bankFilters
 * @param {{min: number, max: number}|null} bounds createdAtMs bounds of the folder
 * @param {?Object} [aiFilter] the AI photo filter, when one applies here
 * @returns {number}
 */
export function countActiveBankFilters(filters, bounds, aiFilter) {
  const active = filters || EMPTY_BANK_FILTERS;
  let count = 0;
  if (hasEntries(active.formatKeys)) count += 1;
  if (hasEntries(active.authorIds)) count += 1;
  if (hasEntries(active.mediaTagIds)) count += 1;
  if (isDateRangeActive(active.dateRange, bounds)) count += 1;
  if (aiFilter) count += 1;
  return count;
}

const toSet = (value) => {
  if (value instanceof Set) return value;
  return new Set(Array.isArray(value) ? value : []);
};

/**
 * Applies search text and the facet filters (AND across facets) to BankItems.
 * Returns the very same array reference when nothing is active so memoized
 * consumers keep their identity.
 * @param {Array<Object>} items BankItems
 * @param {Object} options
 * @param {Object} [options.filters] bankFilters
 * @param {string} [options.searchText]
 * @param {Set<string>|Array<string>} [options.tagFilteredFileIds] file ids matching the selected media tags
 * @param {{min: number, max: number}|null} [options.bounds] createdAtMs bounds of the folder
 * @param {Set<string>|Array<string>} [options.aiFileIds] file ids the AI photo filter keeps; empty means no AI filter
 * @param {Set<string>|Array<string>} [options.aiFolderIds] folders that hold those photos, and their ancestors
 * @returns {Array<Object>}
 */
export function filterBankItems(items, options = {}) {
  const list = Array.isArray(items) ? items : [];
  const { filters, searchText, tagFilteredFileIds, bounds } = options;
  const active = filters || EMPTY_BANK_FILTERS;
  // The AI filter is a set of photos, and a folder is not one of them — but a
  // folder the way to them is what the user needs to get there, so the folders
  // holding matches (and their ancestors, see the selector) come with it. A
  // folder outside that set goes, like any other non-match.
  const aiFileIds =
    options.aiFileIds && (options.aiFileIds.size || options.aiFileIds.length)
      ? toSet(options.aiFileIds)
      : null;
  const aiFolderIds = aiFileIds ? toSet(options.aiFolderIds) : null;

  const query = normalizeSearchText(searchText);
  const formatKeys = hasEntries(active.formatKeys)
    ? new Set(active.formatKeys)
    : null;
  const authorIds = hasEntries(active.authorIds)
    ? new Set(active.authorIds)
    : null;
  const dateRange = isDateRangeActive(active.dateRange, bounds)
    ? {
        start: toBoundary(active.dateRange.start, -Infinity),
        end: toBoundary(active.dateRange.end, Infinity),
      }
    : null;
  const tagFileIds = hasEntries(active.mediaTagIds)
    ? toSet(tagFilteredFileIds)
    : null;

  if (
    !query &&
    !formatKeys &&
    !authorIds &&
    !dateRange &&
    !tagFileIds &&
    !aiFileIds
  ) {
    return items;
  }

  return list.filter((item) => {
    if (aiFileIds) {
      const kept =
        item.type === 'FILE'
          ? aiFileIds.has(item.id)
          : aiFolderIds.has(item.id);
      if (!kept) return false;
    }
    if (query && !(item.searchText || '').includes(query)) return false;
    if (formatKeys && !formatKeys.has(item.formatKey)) return false;
    if (authorIds && !(item.author && authorIds.has(item.author.id))) {
      return false;
    }
    if (
      dateRange &&
      !(
        item.createdAtMs >= dateRange.start && item.createdAtMs <= dateRange.end
      )
    ) {
      return false;
    }
    if (tagFileIds && !(item.type === 'FILE' && tagFileIds.has(item.id))) {
      return false;
    }
    return true;
  });
}
